import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadSimple, CircleNotch, ShieldCheck, CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const FIELD_LABELS = { external_id: "Booking ID", date: "Date *", start_time: "Start time", end_time: "End time", client_name: "Client name *", client_email: "Client email", service: "Service", equipment: "Equipment / room", status: "Status", amount: "Amount paid" };

export function SchedulerImport({ businessId, onDone }) {
  const [csvText, setCsvText] = useState("");
  const [stage, setStage] = useState("file");
  const [meta, setMeta] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [dupMode, setDupMode] = useState("update");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const text = String(reader.result || "");
      setCsvText(text);
      setBusy(true);
      try {
        const { data } = await api.post(`/businesses/${businessId}/wellness/import/preview`, { csv_text: text, mapping: null });
        setMeta(data); setMapping(data.suggested_mapping || {}); setStage("mapping");
      } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't read that file."); }
      finally { setBusy(false); }
    };
    reader.readAsText(f);
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/wellness/import/preview`, { csv_text: csvText, mapping });
      setPreview(data); setStage("preview");
    } catch (err) { toast.error(err?.response?.data?.detail || "Check the mapping."); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/wellness/import/commit`, { csv_text: csvText, mapping, duplicates: dupMode });
      setResult(data); setStage("done");
      toast.success(data.rowan_message);
      onDone && onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed."); }
    finally { setBusy(false); }
  };

  const badge = (r) => r.action === "error"
    ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FDE8E4", color: "#B23B1F" }}>Error</span>
    : r.action === "update"
      ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FFF3DC", color: "#B08840" }}>Duplicate</span>
      : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>New</span>;

  return (
    <div className="bk-card p-5 space-y-4" data-testid="scheduler-import">
      <div>
        <p className="font-semibold" style={{ color: "var(--bk-text)" }}>Import your schedule from another booking system</p>
        <p className="text-xs mt-0.5" style={label}>Upload a CSV export. Only booking and payment data comes over — treatment notes, diagnoses, and health details are automatically excluded. Re-importing the same file updates instead of duplicating.</p>
      </div>

      {stage === "file" && (
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold cursor-pointer bk-btn-primary w-fit" data-testid="import-file-label">
          {busy ? <CircleNotch size={15} className="animate-spin" /> : <UploadSimple size={15} weight="bold" />} Choose CSV file
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-file-input" />
        </label>
      )}

      {stage === "mapping" && meta && (
        <div className="space-y-3" data-testid="import-mapping">
          <p className="text-sm rounded-xl border px-4 py-3" style={{ borderColor: "rgba(232,168,56,0.5)", background: "#FFF9F0", color: "var(--bk-text)" }} data-testid="import-health-note">
            <ShieldCheck size={14} weight="fill" color="#E8A838" className="inline mr-1" />{meta.rowan_note} ({meta.total_rows} rows found)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {meta.fields.map((f) => (
              <label key={f} className="text-xs" style={label}>
                {FIELD_LABELS[f]}
                <select value={mapping[f] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))} data-testid={`import-map-${f}`}
                  className="w-full mt-0.5 rounded-md border px-2 py-1.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <option value="">— not in file —</option>
                  {meta.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          <button onClick={runPreview} disabled={busy || !mapping.client_name || !mapping.date} data-testid="import-preview-btn"
            className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : null} Preview import
          </button>
        </div>
      )}

      {stage === "preview" && preview && (
        <div className="space-y-3" data-testid="import-preview">
          <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }} data-testid="import-summary">
            {preview.summary.create} new · {preview.summary.update} duplicate{preview.summary.update === 1 ? "" : "s"} · {preview.summary.error} error{preview.summary.error === 1 ? "" : "s"} · {preview.summary.will_deduct} completed session{preview.summary.will_deduct === 1 ? "" : "s"} will deduct from packages
          </p>
          <div className="max-h-64 overflow-y-auto space-y-1.5 border rounded-xl p-3" style={{ borderColor: "var(--bk-border)" }}>
            {preview.rows.map((r) => (
              <div key={r.row} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`import-row-${r.row}`} style={{ color: "var(--bk-text)" }}>
                {badge(r)}
                <span className="font-semibold">{r.data.client_name || "?"}</span>
                <span style={label}>{r.data.service_name || r.data.equipment} · {r.data.date} {r.data.start_time} · {r.data.status}{r.data.amount ? ` · $${r.data.amount}` : ""}</span>
                {r.will_deduct && <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>−1 from {r.credit_item}</span>}
                {r.error && <span style={{ color: "#B23B1F" }}>{r.error}</span>}
              </div>
            ))}
          </div>
          {preview.summary.update > 0 && (
            <div className="flex flex-wrap gap-3 text-sm" data-testid="import-dup-choice" style={{ color: "var(--bk-text)" }}>
              <span className="font-semibold">Duplicates:</span>
              {[["update", "Update existing"], ["skip", "Skip them"], ["create_new", "Create anyway"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="dupmode" checked={dupMode === v} onChange={() => setDupMode(v)} data-testid={`import-dup-${v}`} /> {l}
                </label>
              ))}
            </div>
          )}
          <button onClick={commit} disabled={busy} data-testid="import-commit-btn"
            className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <CheckCircle size={15} weight="bold" />}
            Import {preview.summary.create + (dupMode === "skip" ? 0 : preview.summary.update)} bookings
          </button>
        </div>
      )}

      {stage === "done" && result && (
        <div className="space-y-2" data-testid="import-result">
          <p className="text-sm font-semibold" style={{ color: "var(--bk-success)" }}>{result.rowan_message}</p>
          {result.errors?.length > 0 && (
            <div className="rounded-xl border p-3 space-y-1" style={{ borderColor: "rgba(178,59,31,0.4)" }} data-testid="import-errors">
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs" style={{ color: "#B23B1F" }}><WarningCircle size={12} weight="fill" className="inline mr-1" />Row {e.row}: {e.error}</p>
              ))}
            </div>
          )}
          <button onClick={() => { setStage("file"); setResult(null); setPreview(null); setMeta(null); setCsvText(""); }} data-testid="import-again-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
            <ArrowsClockwise size={13} weight="bold" /> Import another file
          </button>
        </div>
      )}
    </div>
  );
}
